import firebase from "firebase";
import { PgnParser } from "../../pgn/PgnParser";
import { ContentStatus, Scenario, Topic, } from "../../types/topics";
import { TopicServiceInterface } from "./TopicServiceInterface";
export class TopicServiceFirestore extends TopicServiceInterface {
    TOPICS_COLLECTION = "topics";
    static SCENARIOS_SUBCOLLECTION = "scenarios";
    firestore;
    constructor(firestore) {
        super();
        this.firestore = firestore;
    }
    // Topic Query Methods
    async getTopics() {
        const snapshot = await this.firestore
            .collection(this.TOPICS_COLLECTION)
            .get();
        return this.convertSnapshotToTopics(snapshot);
    }
    async getTopicsByIds(topicIds) {
        if (!topicIds.length)
            return [];
        // Firestore has a limit of 10 items for 'in' queries
        const chunks = this.chunkArray(topicIds, 10);
        const snapshots = await Promise.all(chunks.map((chunk) => this.firestore
            .collection(this.TOPICS_COLLECTION)
            .where(firebase.firestore.FieldPath.documentId(), "in", chunk)
            .get()));
        const topics = [];
        snapshots.forEach((snapshot) => {
            topics.push(...this.convertSnapshotToTopics(snapshot));
        });
        return topics;
    }
    async getPublicTopics() {
        const snapshot = await this.firestore
            .collection(this.TOPICS_COLLECTION)
            .where("isPublic", "==", true)
            .get();
        return this.convertSnapshotToTopics(snapshot);
    }
    async getUserTopics(userId) {
        const snapshot = await this.firestore
            .collection(this.TOPICS_COLLECTION)
            .where("ownerId", "==", userId)
            .get();
        return this.convertSnapshotToTopics(snapshot);
    }
    async getPublicAndUserTopics(userId) {
        const [publicSnapshot, userSnapshot] = await Promise.all([
            this.firestore
                .collection(this.TOPICS_COLLECTION)
                .where("isPublic", "==", true)
                .get(),
            this.firestore
                .collection(this.TOPICS_COLLECTION)
                .where("ownerId", "==", userId)
                .get(),
        ]);
        console.log("getPublicAndUserTopics", publicSnapshot.size, userSnapshot.size);
        const topics = new Map();
        // Process public topics first, then user topics (which will override if same ID)
        [publicSnapshot, userSnapshot].forEach((snapshot) => {
            snapshot.docs.forEach((doc) => {
                const topic = this.convertDocToTopic(doc);
                topics.set(topic.id, topic);
            });
        });
        return this.sortTopics(Array.from(topics.values()));
    }
    // Topic Mutation Methods
    async createTopic(topicData) {
        const newTopic = new Topic(topicData);
        const topicForFirestore = this.prepareTopicForFirestore(newTopic, true);
        await this.firestore
            .collection(this.TOPICS_COLLECTION)
            .doc(newTopic.id)
            .set(topicForFirestore);
        return newTopic;
    }
    async updateTopic(topicId, topicData) {
        const topicRef = this.firestore
            .collection(this.TOPICS_COLLECTION)
            .doc(topicId);
        const doc = await topicRef.get();
        if (!doc.exists) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        const topic = this.convertDocToTopic(doc);
        // Create a new data object without protected fields
        const allowedData = { ...topicData };
        delete allowedData.isPublic;
        delete allowedData.ownerId;
        delete allowedData.owner;
        delete allowedData.originalTopicId;
        topic.update(allowedData);
        const topicForFirestore = this.prepareTopicForFirestore(topic, false);
        await topicRef.update(topicForFirestore);
        return topic;
    }
    // Scenario Methods
    async getScenarios(topicId) {
        const snapshot = await this.firestore
            .collection(this.TOPICS_COLLECTION)
            .doc(topicId)
            .collection(TopicServiceFirestore.SCENARIOS_SUBCOLLECTION)
            .get();
        return snapshot.docs
            .map((doc) => this.convertDocToScenario(doc, topicId))
            .sort((a, b) => a.order - b.order);
    }
    async getAllScenarios(options) {
        const { page = 1, limit = 20, topicIds } = options || {};
        let allScenarios = [];
        try {
            if (topicIds && topicIds.length > 0) {
                // If specific topic IDs are provided, get scenarios only from those topics
                for (const topicId of topicIds) {
                    try {
                        const scenarios = await this.getScenarios(topicId);
                        allScenarios = [...allScenarios, ...scenarios];
                    }
                    catch (error) {
                        console.error(`Error getting scenarios for topic ${topicId}:`, error);
                    }
                }
            }
            else {
                // Otherwise, get scenarios from all topics
                const topics = await this.getTopics();
                for (const topic of topics) {
                    try {
                        const scenarios = await this.getScenarios(topic.id);
                        allScenarios = [...allScenarios, ...scenarios];
                    }
                    catch (error) {
                        console.error(`Error getting scenarios for topic ${topic.id}:`, error);
                    }
                }
            }
            // Sort scenarios by updatedAt (newest first)
            allScenarios.sort((a, b) => {
                if (a.updatedAt && b.updatedAt) {
                    return b.updatedAt.getTime() - a.updatedAt.getTime();
                }
                return 0;
            });
            // Apply pagination
            const startIndex = (page - 1) * limit;
            const endIndex = startIndex + limit;
            return allScenarios.slice(startIndex, endIndex);
        }
        catch (error) {
            console.error("Error in getAllScenarios:", error);
            return [];
        }
    }
    async createScenario(topicId, scenarioData) {
        const topicRef = await this.getTopicRefOrThrow(topicId);
        const newScenario = new Scenario(topicId, scenarioData);
        const scenarioForFirestore = this.prepareScenarioForFirestore(newScenario, true);
        await topicRef
            .collection(TopicServiceFirestore.SCENARIOS_SUBCOLLECTION)
            .doc(newScenario.id)
            .set(scenarioForFirestore);
        return newScenario;
    }
    async updateScenario(topicId, scenarioId, scenarioData) {
        const scenarioRef = this.getScenarioRef(topicId, scenarioId);
        const scenarioDoc = await scenarioRef.get();
        if (!scenarioDoc.exists) {
            throw new Error(`Scenario with id ${scenarioId} not found in topic ${topicId}`);
        }
        const scenario = this.convertDocToScenario(scenarioDoc, topicId);
        scenario.update(scenarioData);
        const scenarioForFirestore = this.prepareScenarioForFirestore(scenario, false);
        await scenarioRef.update(scenarioForFirestore);
        return scenario;
    }
    async reorderScenarios(topicId, fromIndex, toIndex) {
        const scenarios = await this.getScenarios(topicId);
        const reorderedScenarios = Scenario.reorder(scenarios, fromIndex, toIndex);
        const batch = this.firestore.batch();
        reorderedScenarios.forEach((scenario) => {
            const scenarioRef = this.getScenarioRef(topicId, scenario.id);
            batch.update(scenarioRef, {
                order: scenario.order,
                updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
            });
        });
        await batch.commit();
        return reorderedScenarios;
    }
    async importScenarios(topicId, pgnText) {
        await this.getTopicRefOrThrow(topicId);
        const existingScenarios = await this.getScenarios(topicId);
        const result = { created: [], existing: [] };
        const games = pgnText
            .split(PgnParser.REGEX.GAME_SPLITTER)
            .filter((game) => PgnParser.isValidPgn(game));
        const batch = this.firestore.batch();
        for (const game of games) {
            const normalizedPgn = PgnParser.normalize(game);
            const existingScenario = existingScenarios.find((scenario) => PgnParser.normalize(scenario.pgn) === normalizedPgn);
            if (existingScenario) {
                result.existing.push(existingScenario);
                continue;
            }
            const newScenario = new Scenario(topicId, {
                pgn: game,
                order: Scenario.getNextOrder(existingScenarios),
            });
            const scenarioRef = this.getScenarioRef(topicId, newScenario.id);
            const scenarioForFirestore = this.prepareScenarioForFirestore(newScenario, true);
            batch.set(scenarioRef, scenarioForFirestore);
            result.created.push(newScenario);
        }
        if (result.created.length > 0) {
            await batch.commit();
        }
        return result;
    }
    async cloneToPublic(topicId, userId, owner) {
        const topicRef = await this.getTopicRefOrThrow(topicId);
        const doc = await topicRef.get();
        const originalTopic = this.convertDocToTopic(doc);
        // Create new public topic data
        const publicTopicData = {
            title: `${originalTopic.title} (from Private)`,
            description: originalTopic.description,
            difficultyLevel: originalTopic.difficultyLevel,
            status: ContentStatus.Draft,
            isPublic: true,
            ownerId: userId,
            owner: owner,
            tags: [...originalTopic.tags],
            urls: [...originalTopic.urls],
            workbookRef: originalTopic.workbookRef,
            originalTopicId: originalTopic.id,
        };
        // Create the public topic
        const publicTopic = await this.createTopic(publicTopicData);
        // Get all scenarios from original topic
        const scenarios = await this.getScenarios(topicId);
        // Create scenarios in the new topic
        if (scenarios.length > 0) {
            const pgnText = scenarios.map((s) => s.pgn).join("\n\n");
            await this.importScenarios(publicTopic.id, pgnText);
        }
        return publicTopic;
    }
    // Private Helper Methods
    convertSnapshotToTopics(snapshot) {
        return this.sortTopics(snapshot.docs.map((doc) => this.convertDocToTopic(doc)));
    }
    convertDocToTopic(doc) {
        const data = doc.data();
        if (!data) {
            throw new Error(`No data found for topic ${doc.id}`);
        }
        const topic = new Topic({
            title: data.title,
            description: data.description,
            difficultyLevel: data.difficultyLevel,
            status: data.status,
            isPublic: data.isPublic,
            ownerId: data.ownerId,
            owner: data.owner,
            tags: data.tags,
            urls: data.urls,
            workbookRef: data.workbookRef,
            originalTopicId: data.originalTopicId,
        });
        topic.id = doc.id;
        return topic;
    }
    convertDocToScenario(doc, topicId) {
        const data = doc.data();
        if (!data) {
            throw new Error(`No data found for scenario ${doc.id}`);
        }
        const scenarioData = {
            pgn: data.pgn,
            status: data.status,
            order: data.order,
            createdAt: data.createdAt?.toDate(),
            updatedAt: data.updatedAt?.toDate(),
        };
        const scenario = new Scenario(topicId, scenarioData);
        scenario.id = doc.id;
        return scenario;
    }
    prepareTopicForFirestore(topic, isNew) {
        const data = {
            title: topic.title,
            description: topic.description || "", // Add default empty string
            difficultyLevel: topic.difficultyLevel || null, // Add default null
            status: topic.status,
            tags: topic.tags || [], // Add default empty array
            urls: topic.urls || [], // Add default empty array
            workbookRef: topic.workbookRef || null, // Add default null
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        if (isNew) {
            // Only set these fields during creation
            data.isPublic = topic.isPublic ?? false; // Add default false
            data.ownerId = topic.ownerId;
            data.owner = topic.owner || null; // Add default null
            data.originalTopicId = topic.originalTopicId || null; // Add default null
            data.createdAt = firebase.firestore.FieldValue.serverTimestamp();
        }
        return data;
    }
    prepareScenarioForFirestore(scenario, isNew) {
        const base = {
            title: scenario.title,
            isPuzzle: scenario.isPuzzle,
            isHomework: scenario.isHomework,
            pgn: scenario.pgn,
            order: scenario.order,
            status: scenario.status,
            updatedAt: firebase.firestore.FieldValue.serverTimestamp(),
        };
        if (isNew) {
            return {
                ...base,
                id: scenario.id,
                createdAt: firebase.firestore.FieldValue.serverTimestamp(),
            };
        }
        return base;
    }
    async getTopicRefOrThrow(topicId) {
        const topicRef = this.firestore
            .collection(this.TOPICS_COLLECTION)
            .doc(topicId);
        const doc = await topicRef.get();
        if (!doc.exists) {
            throw new Error(`Topic with id ${topicId} not found`);
        }
        return topicRef;
    }
    getScenarioRef(topicId, scenarioId) {
        return this.firestore
            .collection(this.TOPICS_COLLECTION)
            .doc(topicId)
            .collection(TopicServiceFirestore.SCENARIOS_SUBCOLLECTION)
            .doc(scenarioId);
    }
    sortTopics(topics) {
        return topics.sort((a, b) => {
            if (a.difficultyLevel && b.difficultyLevel) {
                const difficultyComparison = a.difficultyLevel.localeCompare(b.difficultyLevel);
                if (difficultyComparison !== 0)
                    return difficultyComparison;
            }
            else if (a.difficultyLevel) {
                return -1;
            }
            else if (b.difficultyLevel) {
                return 1;
            }
            return a.title.localeCompare(b.title);
        });
    }
    chunkArray(array, size) {
        const chunks = [];
        for (let i = 0; i < array.length; i += size) {
            chunks.push(array.slice(i, i + size));
        }
        return chunks;
    }
}
